import { Ref, RefBool, RefObject, RefString } from '@tblabs/truffle';
import { BlobPicture } from "../Models/BlobPicture";
import { ICamera } from '../Services/Camera/ICamera';
import { Snap } from '../Models/Snap';
import { SelectableCollection } from '../Components/Snapshots/SelectableCollection';
import { PreviewMode } from '../Models/PreviewMode';
import { SnapStorage } from '../Services/Storage/SnapStorage';
import { CameraPictureControl } from '../Services/Camera/Control/CameraPictureControl';
import { Preset } from "../Services/Camera/Control/Preset";
import { ConnectionStatusEvent } from '../Services/ConnectionStatusEvent';
import { Status } from '../Services/Status/Status';
import { StatusEvent } from '../Services/Status/StatusEvent';
import { DeviceConfigForWatcher, DeviceSettingsForWatcher } from '../Models/DeviceConfig';
import { IServer } from '../Services/IServer';
import { Beepers } from "../Services/Beepers";


export class ReceiverLogic
{
    private currentPicture = new RefObject<BlobPicture | null>(null);
    private newestPicture: BlobPicture | null = null; 
    public AllowTakingSnapshot = new RefBool(false);
    public AllowRestorePreview = new RefBool(false);
    public Mode = new Ref<PreviewMode>(PreviewMode.LivePreview);
    public ShowOcrBox = new RefBool(false);
    public OcrText = new RefString();

    public get Snapshots(): SelectableCollection<Snap>
    {
        return this._storage.Items;
    }

    constructor(
        private _server: IServer,
        private _camera: ICamera,
        private _storage: SnapStorage,
        private _pictureControl: CameraPictureControl,
        private _status: Status,
        private _beepers: Beepers)
    {
        this._server.OnConnectionChange.Subscribe((status: ConnectionStatusEvent) =>
        {
            this._status.Set(status.IsConnected ? StatusEvent.ServerConnected : StatusEvent.ServerDisconnected);
        });

        this.Mode.OnChange(mode =>
        {
            if (mode == PreviewMode.LivePreview)
                this.LivePreviewMode();
            else if (mode == PreviewMode.SnapshotView)
                this.SnapshotMode();
        });

        this._camera.OnConnectionChange.Subscribe((status: ConnectionStatusEvent) =>
        {
            this._status.Set(status.IsConnected ? StatusEvent.CameraConnected : StatusEvent.CameraDisconnected);

            this._beepers.CameraConnection(status.IsConnected);
        });

        this._camera.OnNewPicture.Subscribe(async (picture: BlobPicture) =>
        {
            this.newestPicture = picture;

            this._status.PictureReceived(picture.IndexFromDevice);
            this._beepers.NewPicture(this.Mode.value);

            if (this.Mode.Is(PreviewMode.SnapshotView))
                return;

            this.currentPicture.object = picture;
            this.AllowTakingSnapshot.True();
        });

        this._camera.OnConfigReceived.Subscribe((config: DeviceConfigForWatcher) =>
        {
            this._pictureControl.Set(config);

            if (config.Halt)
                this.AllowRestorePreview.True();
        });

        this._pictureControl.ThrottledOnChange.Subscribe((preset: Preset) =>
        {
            this._camera.UpdateSettings({
                Picture: {
                    Name: preset.Name,
                    Width: preset.Width,
                    Height: preset.Height,
                    Brightness: preset.Brightness,
                    Contrast: preset.Contrast,
                    Rotation: preset.Rotation
                }
            } as DeviceSettingsForWatcher);

            this.LivePreviewModeSetup();
        });

        this._storage.Items.OnSelect.Subscribe((snap: Snap) =>
        {
            this.Mode.value = PreviewMode.SnapshotView;
            this.currentPicture.object = snap.Picture;
            this.AllowTakingSnapshot.False();
        });

        this._storage.OnOcrReady.Subscribe((snap: Snap) =>
        {
            this.ShowOcrBox.True();
            this.OcrText.value = snap.OcrText ?? "(brak tekstu na zdjęciu lub nie udało się go odczytać)";
        });
    }

    public LivePreviewMode(): void
    {
        this._camera.Unhalt();
        this.LivePreviewModeSetup();
    }

    public LivePreviewModeSetup(): void
    {
        this.Mode.value = PreviewMode.LivePreview;
        this.currentPicture.object = this.newestPicture;
        this._storage.Items.Deselect();
        this.AllowTakingSnapshot.value = this.IsNewPictureAvailable && !this.IsNewestPictureAlreadyInSnapshotsStorage;
        this.AllowRestorePreview.False();
    }

    private get IsNewPictureAvailable(): boolean
    {
        return !!this.newestPicture;
    }

    private get IsNewestPictureAlreadyInSnapshotsStorage(): boolean
    {
        return this._storage.Items.Find(item => item.Item.Picture == this.newestPicture) !== undefined;
    }

    public SnapshotMode(): void
    {
        this.Mode.value = PreviewMode.SnapshotView;
        this.AllowRestorePreview.True();
    }

    public async TakeSnapshot(): Promise<void>
    {
        if (this.currentPicture.object == null)
            return;
        if (this.AllowTakingSnapshot.IsFalse())
            return;

        await this._storage.Add(this.currentPicture.object);
        this.AllowTakingSnapshot.False();
    }

    public get CameraIsHalted(): boolean
    {
        return this._camera.IsHalted;
    }

    public HaltCamera(): void
    {
        this._camera.Halt();
        this.Mode.value = PreviewMode.SnapshotView;
        this._status.Set(StatusEvent.CameraHalted);
    }

    public UnhaltCamera(): void
    {
        this._camera.Unhalt();
    }

    public get PictureControl(): CameraPictureControl
    {
        return this._pictureControl;
    }

    public get PreviewStream(): RefObject<BlobPicture | null>
    {
        return this.currentPicture;
    }

    public get PreviewStreamImage(): Blob | null
    {
        return this.currentPicture.object?.Image ?? null;
    }
}
